<template>
  <div class="fc-notification-form fc-notification-form--stack">
    <NotificationConfigureHeader
      :title="copy.title"
      :subtitle="copy.subtitle"
      kind="schedule"
      :icon="icon"
    />
    <NotificationNameField
      :value="name"
      :auto-name="autoName"
      :is-custom="nameIsCustom"
      :error="nameError"
      @input="name = $event"
      @update:isCustom="nameIsCustom = $event"
      @reset="resetName"
    />
    <p class="fc-notification-form__hint">{{ nameHint }}</p>
    <NotificationEnableRow
      v-model="flag"
      :hint="copy.enableHint"
      :disabled="disabled"
    />
    <DueDateOffsetField
      ref="offsetField"
      v-model="offset"
      :label="copy.offsetLabel"
      :error="offsetError"
      :disabled="disabled"
    />
    <p class="fc-notification-form__hint">{{ copy.offsetHint }}</p>
    <div class="fc-notification-form__field">
      <ScheduledAtField
        v-model="startTime"
        label="Scheduled at"
        compact-timezone
        :error="scheduleError"
        :disabled="disabled"
        data-testid="upcoming-due-form__scheduled-at"
      />
    </div>
    <div class="fc-notification-form__field fc-notification-form__field--chip-wrap">
      <span class="fc-notification-form__label">{{ locationLabel }}</span>
      <ChipMultiSelect
        v-model="selectedLocations"
        :items="locations"
        item-text="name"
        item-value="_id"
        outlined
        :error="locationsError"
        :disabled="disabled"
        data-testid="upcoming-due-form__locations"
        data-testid-prefix="upcoming-due-form__locations"
      />
    </div>
    <div class="fc-notification-meta">
      <span class="fc-notification-chip">
        <v-icon x-small>mdi-account-outline</v-icon>
        {{ copy.sentTo }}
      </span>
    </div>
    <div class="fc-notification-infobox">
      <v-icon small>mdi-information-outline</v-icon>
      <p>
        When a cycle is created, the earliest task due date of each location is found and one email
        per location is scheduled for N working days before it.
        <b>Due-date changes after cycle creation do not move the email.</b>
        Each user receives only their tasks.
      </p>
    </div>
  </div>
</template>

<script>
import ChipMultiSelect from '../../../../components/ChipMultiSelect/ChipMultiSelect.vue';
import {
  DueDateOffsetField,
  NotificationConfigureHeader,
  NotificationEnableRow,
  NotificationNameField,
  ScheduledAtField,
} from '../../../../components/Notifications';
import {
  NOTIFICATION_FEATURE_ICON,
  NOTIFICATION_FORM_COPY,
  NOTIFICATION_TYPE_KEYS,
} from '../../../../utils/constants';
import { buildNotificationName } from '../../../../utils/notificationNameUtils';
import { localStartTimeFromSchedule } from '../../../../utils/notificationScheduleUtils';
import notificationFormErrorMixin from './notificationFormErrorMixin';

export default {
  name: 'UpcomingDueForm',
  components: {
    ChipMultiSelect,
    DueDateOffsetField,
    NotificationConfigureHeader,
    NotificationEnableRow,
    NotificationNameField,
    ScheduledAtField,
  },
  mixins: [notificationFormErrorMixin],
  props: {
    value: { type: Object, default: () => ({}) },
    locations: { type: Array, default: () => [] },
    serverErrors: { type: Object, default: () => ({}) },
    disabled: { type: Boolean, default: false },
  },
  data() {
    const existingOffset = this.value.meta?.earliestDueDateOffsetWorkingDays;
    return {
      name: this.value.name || '',
      nameIsCustom: this.value.name_is_custom === true,
      flag: this.value.flag !== false,
      offset: existingOffset == null ? -2 : existingOffset,
      startTime: localStartTimeFromSchedule(this.value.meta?.schedule),
      selectedLocations: [...(this.value.meta?.locations || [])],
      copy: NOTIFICATION_FORM_COPY.upcomingDueByLocation,
      nameHint: NOTIFICATION_FORM_COPY.NAME_HINT,
      locationLabel: NOTIFICATION_FORM_COPY.LOCATION,
      icon: NOTIFICATION_FEATURE_ICON.upcomingDueByLocation,
    };
  },
  computed: {
    autoName() {
      return buildNotificationName(
        NOTIFICATION_TYPE_KEYS.UPCOMING_DUE_BY_LOCATION,
        { locations: this.selectedLocations },
        this.locations
      );
    },
  },
  methods: {
    resetName() {
      this.nameIsCustom = false;
      this.name = this.autoName;
    },
    validate() {
      return this.$refs.offsetField ? this.$refs.offsetField.validate() : true;
    },
    toPayload() {
      return {
        featureDetails: {
          _id: this.value._id,
          feature: NOTIFICATION_TYPE_KEYS.UPCOMING_DUE_BY_LOCATION,
          flag: this.flag,
          name: this.nameIsCustom ? this.name : this.autoName,
          name_is_custom: this.nameIsCustom,
          meta: {
            locations: this.selectedLocations,
            earliestDueDateOffsetWorkingDays: this.offset,
            schedule: { startTime: this.startTime },
          },
        },
      };
    },
  },
};
</script>
